import { AwsClient } from 'aws4fetch';
import type { AwsCredentials } from './awsCredentials';

const API_URL = import.meta.env.VITE_API_URL;
const AWS_REGION = import.meta.env.VITE_AWS_REGION;

// A limit the backend answers with a readable message (402: the workspace reached its trial
// usage or contact limit) surfaces as that message; anything else keeps the status-only error.
async function apiError(res: Response, method: string, path: string): Promise<Error> {
  if (res.status === 402) {
    try {
      const body = (await res.json()) as { error?: unknown };
      if (typeof body?.error === 'string' && body.error) return new Error(body.error);
    } catch {
      // Not JSON: fall through to the status-only error.
    }
  }
  return new Error(`API ${res.status} on ${method} ${path}`);
}

function makeClient(credentials: AwsCredentials): AwsClient {
  return new AwsClient({
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey,
    sessionToken: credentials.sessionToken,
    region: AWS_REGION,
    service: 'execute-api',
  });
}

// Unauthenticated GET, for the routes API Gateway exposes with `auth: NONE` (today the
// pre-login tenant theme). No SigV4 client: the login screens have no Cognito credentials
// yet, and signing with none would fail the request outright.
export async function publicGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw await apiError(res, 'GET', path);
  return res.json() as Promise<T>;
}

export async function signedGet<T>(path: string, credentials: AwsCredentials): Promise<T> {
  const res = await makeClient(credentials).fetch(`${API_URL}${path}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw await apiError(res, 'GET', path);
  return res.json() as Promise<T>;
}

export async function signedPost<T>(
  path: string,
  body: unknown,
  credentials: AwsCredentials,
): Promise<T> {
  const res = await makeClient(credentials).fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res, 'POST', path);
  return res.json() as Promise<T>;
}

export async function signedPut<T>(
  path: string,
  body: unknown,
  credentials: AwsCredentials,
): Promise<T> {
  const res = await makeClient(credentials).fetch(`${API_URL}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res, 'PUT', path);
  return res.json() as Promise<T>;
}

export async function signedDelete<T>(
  path: string,
  credentials: AwsCredentials,
): Promise<T> {
  const res = await makeClient(credentials).fetch(`${API_URL}${path}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw await apiError(res, 'DELETE', path);
  return res.json() as Promise<T>;
}
